import { useState, type FormEvent } from "react";
import { ChevronDown, Clock, Instagram, Mail, MapPin, Phone, Telegram } from "./icons";
import Breadcrumbs from "./Breadcrumbs";
import SectionHeading from "./SectionHeading";
import {
  CONTACT_EMAIL,
  CONTACT_INSTAGRAM_URL,
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
  CONTACT_TELEGRAM_URL,
} from "../data/contact";
import { useLanguage } from "../context/LanguageContext";

export default function ContactPage() {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+7");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const addressValue = t("contactDetailAddressValue");

  const details = [
    {
      icon: MapPin,
      label: t("contactDetailAddress"),
      value: addressValue,
      href: "https://maps.app.goo.gl/2VPjX5mp4WszmhBk9?g_st=iw",
      external: true,
    },
    {
      icon: Phone,
      label: t("contactDetailPhone"),
      value: CONTACT_PHONE,
      href: CONTACT_PHONE_TEL,
      external: false,
    },
    {
      icon: Mail,
      label: t("contactDetailEmail"),
      value: CONTACT_EMAIL,
      href: `mailto:${CONTACT_EMAIL}`,
      external: false,
    },
    {
      icon: Telegram,
      label: "Telegram",
      value: "@barcraft_ltd",
      href: CONTACT_TELEGRAM_URL,
      external: true,
    },
    {
      icon: Instagram,
      label: "Instagram",
      value: "@barcraft.pro",
      href: CONTACT_INSTAGRAM_URL,
      external: true,
    },
    {
      icon: Clock,
      label: t("contactDetailHours"),
      value: t("contactDetailHoursValue"),
      href: null,
      external: false,
    },
  ];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    const form = e.target as HTMLFormElement;
    const botcheck = form.botcheck?.checked;
    if (botcheck) {
      // It's a bot
      setSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone: `${countryCode} ${phone}`,
          company,
          message,
        }),
      });
      if (!res.ok) throw new Error("request failed");
      setSubmitted(true);
      setName("");
      setEmail("");
      setPhone("");
      setCompany("");
      setMessage("");
    } catch {
      setError(t("contactFormError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-white pb-24 pt-24 md:pb-32 md:pt-32">
      <Breadcrumbs items={[{ label: t("contactPageTitle") }]} />
      <div className="container-industrial pt-16">
        <SectionHeading title={t("contactPageTitle")} description={t("contactPageDescription")} />

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr]">
          <ul className="flex flex-col gap-1">
            {details.map(({ icon: Icon, label, value, href, external }) => {
              // Fixed min-height (not just the flex gap) so every icon
              // square sits the same visual distance from the next one —
              // items-center alone made the gap depend on how many lines
              // that row's own text wrapped to (the 3-line address vs. a
              // one-line phone number), stretching some rows taller than
              // others between icons even though the gap between them
              // never changed. 64px fits the address's 3 lines at this
              // text size (a smidge tighter than the address's own natural
              // height — negligible, and worth it for a noticeably
              // tighter rhythm on the other five single-line rows).
              const rowClass = "group flex min-h-16 items-center gap-3";
              const iconClass =
                "flex h-12 w-12 shrink-0 items-center justify-center border border-steel-300 text-accent transition-colors duration-150 ease-engineered";
              // Only the clickable rows (href set) get the interactive
              // hover/press fill — "Часы работы" keeps the resting grey
              // frame but never flashes red, since it isn't a link.
              const iconInteractiveClass =
                "group-hover:border-accent group-hover:bg-accent group-hover:text-white group-active:border-accent group-active:bg-accent group-active:text-white";
              // Telegram's glyph reads visually smaller than its siblings at
              // the same size — bumped up, but kept on the same thin stroke
              // weight as the rest of this row.
              const isTelegram = Icon === Telegram;
              const content = (
                <>
                  <span className={href ? `${iconClass} ${iconInteractiveClass}` : iconClass}>
                    <Icon size={isTelegram ? 38 : 34} strokeWidth={1.4} />
                  </span>
                  <p
                    className={`text-base leading-snug text-ink transition-colors duration-150 ease-engineered ${
                      href ? "group-hover:text-accent" : ""
                    }`}
                  >
                    {value}
                  </p>
                </>
              );

              return (
                <li key={label}>
                  {href ? (
                    <a
                      href={href}
                      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      aria-label={label}
                      className={rowClass}
                    >
                      {content}
                    </a>
                  ) : (
                    <div className={rowClass}>{content}</div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="border border-steel-200 p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <h3 className="font-sans text-xl font-semibold tracking-tight text-ink">
                  {t("contactSuccessTitle")}
                </h3>
                <p className="max-w-sm text-sm leading-relaxed text-steel-600">
                  {t("contactSuccessDesc")}
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="btn-dark-outline mt-2"
                >
                  {t("contactSuccessButton")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-steel-500">
                    {t("contactFormName")}
                  </span>
                  <input
                    required
                    type="text"
                    value={name}
                    placeholder="Иван Иванов"
                    onChange={(e) => setName(e.target.value)}
                    className="border border-steel-300 px-3 py-2.5 text-sm text-ink placeholder-steel-400 outline-none focus:border-ink"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-steel-500">
                    {t("contactFormEmail")}
                  </span>
                  <input
                    required
                    type="email"
                    value={email}
                    placeholder="name@company.com"
                    pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                    onChange={(e) => setEmail(e.target.value)}
                    className="border border-steel-300 px-3 py-2.5 text-sm text-ink placeholder-steel-400 outline-none focus:border-ink"
                  />
                </label>

                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-steel-500">
                    {t("contactFormCompany")}
                  </span>
                  <input
                    type="text"
                    value={company}
                    placeholder="ТОО / ООО Название компании"
                    onChange={(e) => setCompany(e.target.value)}
                    className="border border-steel-300 px-3 py-2.5 text-sm text-ink placeholder-steel-400 outline-none focus:border-ink"
                  />
                </label>

                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-steel-500">Телефон</span>
                  <div className="flex gap-2">
                    {/* shrink-0 keeps this at its natural width regardless
                        of how narrow the viewport gets — without it, the
                        select and the flex-1 input fought over the same
                        space and the input's own min-width:auto (the
                        default for a flex child) pushed the row wider than
                        the screen instead of letting the input shrink.
                        appearance-none + a custom chevron replaces iOS
                        Safari's native rounded-pill <select> chrome, which
                        otherwise ignores the square border entirely. */}
                    <div className="relative shrink-0">
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-full appearance-none border border-steel-300 bg-white py-2.5 pl-3 pr-8 text-sm text-ink outline-none focus:border-ink"
                      >
                        {/* KZ and RU are both dialing code +7 — this
                            site's audience is Kazakhstan, so KZ is listed
                            (and selected by default via countryCode's
                            initial state) ahead of RU rather than folding
                            them into one ambiguous "(KZ/RU)" option. */}
                        <option value="+7">+7 (KZ)</option>
                        <option value="+7">+7 (RU)</option>
                        <option value="+998">+998 (UZ)</option>
                        <option value="+996">+996 (KG)</option>
                        <option value="+971">+971 (AE)</option>
                        <option value="+1">+1 (US)</option>
                      </select>
                      <ChevronDown
                        size={14}
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-steel-400"
                      />
                    </div>
                    <input
                      required
                      type="tel"
                      value={phone}
                      placeholder="701 234 56 78"
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/\D/g, "");
                        setPhone(numericValue);
                      }}
                      className="min-w-0 flex-1 border border-steel-300 px-3 py-2.5 text-sm text-ink placeholder-steel-400 outline-none focus:border-ink"
                    />
                  </div>
                </div>

                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-steel-500">
                    {t("contactFormMessage")}
                  </span>
                  <textarea
                    required
                    rows={5}
                    value={message}
                    placeholder="Опишите ваш проект или задачу..."
                    onChange={(e) => setMessage(e.target.value)}
                    className="resize-none border border-steel-300 px-3 py-2.5 text-sm text-ink placeholder-steel-400 outline-none focus:border-ink"
                  />
                </label>

                {error && <p className="text-sm text-accent sm:col-span-2">{error}</p>}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary sm:col-span-2 mt-2 disabled:opacity-60"
                >
                  {isSubmitting ? "…" : t("contactFormSubmit")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
